// frontend/app/middleware/auth.global.ts
//
// The only awaiter of session.ready: public pages render immediately, while
// protected routes wait here for the bootstrap to settle. Role-gating happens
// before the page loads — the fetch layer never navigates on 403.
// (frontend ADR-0001)
export default defineNuxtRouteMiddleware(async (to) => {
  if (to.meta.public) return
  const session = useSession()
  await session.ready
  const authStore = useAuthStore()
  if (!authStore.isLoggedIn) {
    return navigateTo(`/login?redirect=${encodeURIComponent(to.fullPath)}`)
  }
  const roles = to.meta.roles as string[] | undefined
  if (roles && !roles.includes(authStore.role)) {
    // Some tools (e.g. check-in) are also usable by organizer members — the
    // backend enforces per-competition organizer authz on each action; the page
    // gate just keeps ordinary participants out. `allowOrganizer` lets any
    // organizer member (owner/admin/staff of some org) through.
    if (to.meta.allowOrganizer) {
      const organizerStore = useOrganizerStore()
      if (!organizerStore.organizers.length) await organizerStore.fetchAll({ limit: 100 })
      if (organizerStore.organizers.length === 0) return navigateTo('/403')
    } else {
      return navigateTo('/403')
    }
  }
  // Organizer pages are membership-scoped, not platform-role-scoped: the
  // actor must belong to the organizer in the route. The store holds the
  // actor's own organizers (the backend list endpoint is membership-filtered
  // for non-admins); on a miss we refetch once — that covers both an empty
  // store on direct navigation and a stale store after joining an organizer.
  if (to.params.organizerId && authStore.role !== 'platform_admin') {
    const orgId = decodeId(to.params.organizerId as string)
    const organizerStore = useOrganizerStore()
    const isMember = () => organizerStore.organizers.some(o => o.id === orgId)
    if (!isMember()) {
      // Membership decides a 403, so it must be checked against every
      // organizer, not the API's first page of 20.
      await organizerStore.fetchAll({ limit: 100 })
      if (!isMember()) {
        return navigateTo('/403')
      }
    }
  }
})
